/**
 * Utilities for recurring pattern identification and matching
 * Centralizes pattern key generation and matching logic to avoid duplication
 */

import { normalizeDescription, extractMerchant } from '../analytics/recurring/normalizer';

/**
 * Get a consistent pattern key from a display name
 * Uses the merchant secondary name (first 2 significant words) for matching
 * This allows matching variations like "Disney Plus ECOM..." and "Disney Plus..."
 *
 * @param displayName - The display name from a transaction or pattern
 * @returns A normalized pattern key suitable for matching
 *
 * @example
 * getPatternKey('Disney Plus ECOM 1234') => 'disney plus'
 * getPatternKey('Netflix.com CA') => 'netflix com'
 */
export function getPatternKey(displayName: string): string {
  const normalized = normalizeDescription(displayName);
  const merchant = extractMerchant(normalized);
  return merchant.secondary || merchant.primary || normalized;
}

/**
 * Check if a pattern has been dismissed by the user
 * Checks both the exact display name and the normalized pattern key
 *
 * @param displayName - The display name to check
 * @param dismissedSet - Set of dismissed pattern keys
 * @returns true if the pattern is dismissed
 *
 * @example
 * isPatternDismissed('Disney Plus ECOM', new Set(['disney plus'])) => true
 */
export function isPatternDismissed(
  displayName: string,
  dismissedSet: Set<string>
): boolean {
  const patternKey = getPatternKey(displayName);
  return dismissedSet.has(displayName) || dismissedSet.has(patternKey);
}

/**
 * Check if a pattern has been snoozed by the user
 * Checks both the exact display name and the normalized pattern key
 *
 * @param displayName - The display name to check
 * @param snoozedSet - Set of snoozed pattern keys
 * @returns true if the pattern is snoozed
 *
 * @example
 * isPatternSnoozed('Netflix Subscription', new Set(['netflix subscription'])) => true
 */
export function isPatternSnoozed(
  displayName: string,
  snoozedSet: Set<string>
): boolean {
  const patternKey = getPatternKey(displayName);
  return snoozedSet.has(displayName) || snoozedSet.has(patternKey);
}

/**
 * Convert a list of pattern strings to their normalized keys
 * Useful for loading dismissed/snoozed patterns from storage
 *
 * @param patterns - Array of pattern display names
 * @returns Set of normalized pattern keys
 *
 * @example
 * normalizePatternList(['Disney Plus', 'Netflix.com'])
 *   => Set(['disney plus', 'netflix com'])
 */
export function normalizePatternList(patterns: string[]): Set<string> {
  return new Set(patterns.map(p => getPatternKey(p)));
}
